/**
 * Location Address Display Component
 *
 * Resolves and displays location address with country/state/city names
 * based on the location's addressLang setting.
 *
 * Each location can have a different addressLang, so this component
 * loads the appropriate location data for each location individually.
 *
 * This is similar to ContactAddressDisplay but for location entities
 * (vendor locations, customer locations).
 */

import { useMemo } from 'react';
import { useCascadingLocations, useCountriesData } from '@/hooks/useLocations';
import { formatAddressWithResolvedNames } from '@/utils/locationUtils';
import { AddressParseFailedTag } from '@/components/common/AddressParseFailedTag';
import type { AddressParseStatus } from '@/hooks/mockParseAddress';

export interface LocationAddressData {
  countryId?: number | null;
  stateId?: number | null;
  cityId?: number | null;
  address?: string | null;
  postcode?: string | null;
  addressLang?: string | null;
  /** board2-#79/#101: backend address-parse status — 'failed' renders a warning Tag next to the raw address */
  parseStatus?: AddressParseStatus | null;
  // Also accept pre-resolved strings from API (optional)
  country?: string | null;
  state?: string | null;
  city?: string | null;
}

interface LocationAddressDisplayProps {
  location: LocationAddressData;
  fallback?: string;
}

/**
 * Displays a location's address with resolved location names.
 * Loads location data based on the location's addressLang.
 *
 * Handles two API patterns:
 * 1. Vendor locations: address field contains full formatted address (already includes city/state/country)
 * 2. Customer locations: address field contains raw address, separate city/state/country strings
 *
 * We detect which pattern by checking if the address already contains the location info.
 */
export function LocationAddressDisplay({ location, fallback = '-' }: LocationAddressDisplayProps) {
  // Check if API already provides pre-resolved location names (from list API)
  const hasPreResolvedNames = !!(location.city || location.state || location.country);

  // Always call hooks (React rules) but only use them when needed
  // Load countries with location's addressLang for correct localized names
  const { countries } = useCountriesData(location.addressLang ?? undefined);

  // Resolve countryCode from countryId for cascading location fetch
  const countryCode = useMemo(() => {
    if (hasPreResolvedNames || !location.countryId || !countries) return null;
    const country = countries.find((c) => c.countryId === location.countryId);
    return country?.countryCode || null;
  }, [hasPreResolvedNames, location.countryId, countries]);

  // Load states and cities based on location's IDs and addressLang
  const { states, cities } = useCascadingLocations(
    countryCode,
    location.stateId ?? null,
    location.addressLang ?? undefined // Use location's addressLang for name resolution
  );

  // Format the address with resolved names
  const displayAddress = useMemo(() => {
    // If API provides pre-resolved names, check if address already contains them
    if (hasPreResolvedNames) {
      const address = location.address || '';

      // Check if address already contains the city/state/country (vendor API pattern)
      // This avoids duplicating location info that's already in the address field
      const addressContainsLocation =
        (location.city && address.includes(location.city)) ||
        (location.state && address.includes(location.state)) ||
        (location.country && address.includes(location.country));

      if (addressContainsLocation) {
        // Address is already formatted by backend (vendor pattern)
        return address || fallback;
      }

      // Address doesn't contain location info (customer pattern)
      // Build full address from parts
      const parts: string[] = [];
      if (location.address) parts.push(location.address);
      if (location.city) parts.push(location.city);
      if (location.state) parts.push(location.state);
      if (location.postcode) parts.push(location.postcode);
      if (location.country) parts.push(location.country);
      return parts.length > 0 ? parts.join(', ') : fallback;
    }

    // Otherwise, resolve names from IDs (for detail API or manual data)
    return formatAddressWithResolvedNames(
      {
        address: location.address,
        cityId: location.cityId,
        stateId: location.stateId,
        postcode: location.postcode,
        countryId: location.countryId,
      },
      countries || [],
      states || [],
      cities || [],
      fallback
    );
  }, [hasPreResolvedNames, location, countries, states, cities, fallback]);

  // #101 BE-03: an unparseable row shows the raw address + a visible warning
  // Tag instead of a silent pending+dash ('pending' stays tag-less on purpose)
  if (location.parseStatus === 'failed') {
    return (
      <>
        {displayAddress}
        <AddressParseFailedTag />
      </>
    );
  }

  return <>{displayAddress}</>;
}

export default LocationAddressDisplay;
